import React, { useEffect, useRef, useState } from 'react';
import baseURL from '../url';
import './ProductsPage.css';
import 'react-toastify/dist/ReactToastify.css';
import ProductosLoading from '../ProductosLoading/ProductosLoading';
import { Link as Anchor, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import moneda from '../moneda';
import { catalogImageUrl } from '../imageUrl';

export default function ProductsPage() {
    const [productos, setProductos] = useState([]);
    const [secciones, setSecciones] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [subcategorias, setSubCategorias] = useState([]);
    const [loading, setLoading] = useState(true);
    const [seccionSeleccionada, setSeccionSeleccionada] = useState('');
    const [categoriaSeleccionada, setCategoriaSeleccionada] = useState('');
    const [subcategoriaSeleccionada, setSubcategoriaSeleccionada] = useState('');
    const [searchText, setSearchText] = useState('');
    const [precioMinimo] = useState(0);
    const [precioMaximo, setPrecioMaximo] = useState(0);
    const [precioActual, setPrecioActual] = useState(0);
    const [selectedItems, setSelectedItems] = useState([]);
    const [searchParams] = useSearchParams();
    const queryString = searchParams.toString();
    const resultsRef = useRef(null);
    const scrollAfterFilterRef = useRef(false);

    const sameId = (a, b) => String(a ?? '') === String(b ?? '');

    const scheduleFilterScroll = () => {
        scrollAfterFilterRef.current = true;
    };

    const scrollToResults = () => {
        if (!resultsRef.current) return;

        const offset = window.innerWidth <= 1024 ? 88 : 24;
        const targetTop = resultsRef.current.getBoundingClientRect().top + window.pageYOffset - offset;

        window.scrollTo({
            top: Math.max(targetTop, 0),
            behavior: 'smooth',
        });
    };

    useEffect(() => {
        cargarProductos();
        cargarSecciones();
        cargarCategorias();
        cargarSubCategorias();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const cargarProductos = () => {
        fetch(`${baseURL}/productosGet.php`, {
            method: 'GET',
        })
            .then(response => response.json())
            .then(data => {
                const productosData = data.productos || [];
                setProductos(productosData);
                const maxPrecio = calcularPrecioMaximo(productosData);
                setPrecioMaximo(maxPrecio);
                setPrecioActual(maxPrecio);
                setLoading(false);
            })
            .catch(error => console.error('Error al cargar productos:', error));
    };

    const cargarSecciones = () => {
        fetch(`${baseURL}/seccionesGet.php`, {
            method: 'GET',
        })
            .then(response => response.json())
            .then(data => {
                setSecciones(data.secciones || []);
            })
            .catch(error => console.error('Error al cargar secciones:', error));
    };

    const cargarCategorias = () => {
        fetch(`${baseURL}/categoriasGet.php`, {
            method: 'GET',
        })
            .then(response => response.json())
            .then(data => {
                setCategorias(data.categorias || []);
            })
            .catch(error => console.error('Error al cargar categorias:', error));
    };

    const cargarSubCategorias = () => {
        fetch(`${baseURL}/subCategoriaGet.php`, {
            method: 'GET',
        })
            .then(response => response.json())
            .then(data => {
                setSubCategorias(data.subcategorias || []);
            })
            .catch(error => console.error('Error al cargar subcategorias:', error));
    };

    const calcularPrecioMaximo = (productosData) => {
        if (!productosData?.length) {
            return 0;
        }

        return Math.max(...productosData.map(producto => Number(producto.precio || 0)));
    };

    const obtenerImagen = (item) => {
        if (item.imagen1) {
            return item.imagen1;
        } else if (item.imagen2) {
            return item.imagen2;
        } else if (item.imagen3) {
            return item.imagen3;
        } else if (item.imagen4) {
            return item.imagen4;
        }
        return null;
    };

    const handleSearchInputChange = (event) => {
        setSearchText(event.target.value);
    };

    const handlePrecioChange = (event) => {
        const precio = parseFloat(event.target.value);
        setPrecioActual(precio);
    };

    const handleItemSelect = (item) => {
        scheduleFilterScroll();
        if (item === "") {
            setSelectedItems([]);
        } else {
            setSelectedItems([item]);
        }
    };

    const handleSeccionSelect = (event) => {
        scheduleFilterScroll();
        setSeccionSeleccionada(event.target.value);
        setCategoriaSeleccionada('');
        setSubcategoriaSeleccionada('');
    };

    const handleCategoriaSelect = (event) => {
        scheduleFilterScroll();
        setCategoriaSeleccionada(event.target.value);
        setSubcategoriaSeleccionada('');
    };

    const handleSubcategoriaSelect = (event) => {
        scheduleFilterScroll();
        setSubcategoriaSeleccionada(event.target.value);
    };

    useEffect(() => {
        const params = new URLSearchParams(queryString);
        const seccionParam = params.get('seccion') || '';
        const categoriaParam = params.get('categoria') || '';
        const subcategoriaParam = params.get('subcategoria') || '';
        const buscarParam = params.get('buscar') || '';

        const subcategoriaParamData = subcategorias.find(item => sameId(item.idSubCategoria, subcategoriaParam));
        const categoriaFinal = categoriaParam || subcategoriaParamData?.idCategoria || '';
        const categoriaParamData = categorias.find(item => sameId(item.idCategoria, categoriaFinal));
        const seccionFinal = seccionParam || categoriaParamData?.idSeccion || '';

        setSeccionSeleccionada(seccionFinal);
        setCategoriaSeleccionada(categoriaFinal);
        setSubcategoriaSeleccionada(subcategoriaParam);
        setSearchText(buscarParam);

        if (seccionFinal || categoriaFinal || subcategoriaParam || buscarParam) {
            scheduleFilterScroll();
        }
    }, [queryString, categorias, subcategorias]);

    useEffect(() => {
        if (!scrollAfterFilterRef.current || loading) return;

        scrollAfterFilterRef.current = false;
        const scrollTimer = window.setTimeout(scrollToResults, 60);

        return () => window.clearTimeout(scrollTimer);
    }, [seccionSeleccionada, categoriaSeleccionada, subcategoriaSeleccionada, selectedItems, loading]);

    const categoriaDeProducto = (producto) => categorias.find(categoria => sameId(categoria.idCategoria, producto.idCategoria));

    const categoriasFiltradas = categorias.filter(categoria =>
        seccionSeleccionada === '' || sameId(categoria.idSeccion, seccionSeleccionada)
    );

    const subcategoriasFiltradas = subcategorias.filter(subcategoria =>
        categoriaSeleccionada === '' || sameId(subcategoria.idCategoria, categoriaSeleccionada)
    );

    const filterProductos = (producto) => {
        const categoriaProducto = categoriaDeProducto(producto);
        const seccionMatch = seccionSeleccionada === '' || sameId(categoriaProducto?.idSeccion, seccionSeleccionada);
        const categoriaMatch = categoriaSeleccionada === '' || sameId(producto.idCategoria, categoriaSeleccionada);
        const subcategoriaMatch = subcategoriaSeleccionada === '' || sameId(producto.idSubCategoria, subcategoriaSeleccionada);
        const searchTextMatch = searchText === '' || producto.titulo.toLowerCase().includes(searchText.toLowerCase());
        const precioMatch = Number(producto.precio || 0) >= precioMinimo && Number(producto.precio || 0) <= precioActual;
        const itemMatches = selectedItems?.length === 0 || selectedItems.some(item => {
            return producto.item1 === item ||
                producto.item2 === item ||
                producto.item3 === item ||
                producto.item4 === item ||
                producto.item5 === item ||
                producto.item6 === item ||
                producto.item7 === item ||
                producto.item8 === item ||
                producto.item9 === item ||
                producto.item10 === item;
        });
        return seccionMatch && categoriaMatch && subcategoriaMatch && searchTextMatch && precioMatch && itemMatches;
    };

    const productUrl = (item) => `/producto/${item.idProducto}/${item.titulo?.replace(/\s+/g, '-') || 'producto'}`;

    return (
        <div className='ProductsContainPage'>
            {productos?.length > 0 && (
                <div className='filtrosPage'>
                    <div className='searchInput'>
                        {productos?.length > 0 &&
                            <fieldset className="inputSearch">
                                <FontAwesomeIcon icon={faSearch} className="search-icon" />
                                <input
                                    type="text"
                                    placeholder="Buscar..."
                                    value={searchText}
                                    onChange={handleSearchInputChange}
                                    className='input'
                                />
                            </fieldset>
                        }
                    </div>
                    <div className='inputsGrap'>
                        <select
                            value={seccionSeleccionada}
                            onChange={handleSeccionSelect}
                        >
                            <option value="">Secciones...</option>
                            {secciones.map(seccion => (
                                <option key={seccion.idSeccion} value={seccion.idSeccion}>
                                    {seccion.seccion}
                                </option>
                            ))}
                        </select>

                        <select
                            value={categoriaSeleccionada}
                            onChange={handleCategoriaSelect}
                        >
                            <option value="">Categorias...</option>
                            {categoriasFiltradas.map(categoria => (
                                <option key={categoria.idCategoria} value={categoria.idCategoria}>
                                    {categoria.categoria}
                                </option>
                            ))}
                        </select>

                        <select
                            value={subcategoriaSeleccionada}
                            onChange={handleSubcategoriaSelect}
                        >
                            <option value="">Subcategorias...</option>
                            {subcategoriasFiltradas.map(subcategoria => (
                                <option key={subcategoria.idSubCategoria} value={subcategoria.idSubCategoria}>
                                    {subcategoria.subcategoria}
                                </option>
                            ))}
                        </select>

                        <select
                            value={selectedItems.length > 0 ? selectedItems[0] : ""}
                            onChange={(e) => handleItemSelect(e.target.value)}
                        >
                            <option value="">Items...</option>
                            {productos.reduce((items, item) => {
                                for (let i = 1; i <= 10; i++) {
                                    const currentItem = item[`item${i}`];
                                    if (currentItem && !items.includes(currentItem)) {
                                        items.push(currentItem);
                                    }
                                }
                                return items;
                            }, []).map((item, index) => (
                                <option key={index} value={item}>{item}</option>
                            ))}
                        </select>
                    </div>

                    <div className='range'>
                        <input
                            type="range"
                            min={precioMinimo}
                            max={precioMaximo}
                            value={precioActual}
                            onChange={handlePrecioChange}
                        />
                        <div>{moneda} {precioActual.toLocaleString()}</div>
                    </div>
                </div>
            )}
            <div>
                {loading ? (
                    <ProductosLoading />
                ) : (
                    <div className='ProductsGrap' ref={resultsRef}>
                        {productos
                            .filter(filterProductos)
                            .map(item => {
                                const categoriaProducto = categoriaDeProducto(item);

                                return (
                                    <Anchor className='cardProdcutmasVendido' key={item.idProducto} to={productUrl(item)}>
                                        <img src={catalogImageUrl(obtenerImagen(item))} alt="imagen" loading="lazy" decoding="async" />
                                        <div className='cardText'>
                                            <h4>{item.titulo}</h4>
                                            <span>
                                                {categoriaProducto?.seccion ? `${categoriaProducto.seccion} / ` : ''}{categoriaProducto?.categoria}
                                            </span>
                                            <div className='deFLexPrice'>
                                                <h5> {moneda} {String(item?.precio)?.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}</h5>
                                                {item.precioAnterior !== 0 && item.precioAnterior !== undefined && (
                                                    <h5 className='precioTachado'>{moneda} {`${item?.precioAnterior}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}</h5>
                                                )}
                                            </div>
                                        </div>
                                    </Anchor>
                                );
                            })}
                    </div>
                )}
            </div>
        </div>
    );
}
